import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useAutoSave } from "@/hooks/useAutoSave";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Info } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

/* ───────────────────────────────────────── schema ───────────────────────────────────────── */
const basicSettingsSchema = z.object({
  title: z.string().min(1, "Title is required"),
  description: z.string().optional(),
  agent_name: z.string().optional(),
  welcome_message: z.string().optional(),
  conversation_starters: z.string().optional(),
  thank_you_message: z.string().optional(),
  notification_email: z
    .string()
    .email("Please enter a valid email")
    .optional()
    .or(z.literal("")),
  notification_webhook_url: z
    .string()
    .url("Please enter a valid webhook URL")
    .optional()
    .or(z.literal("")),
  allow_web_search: z.enum(["disabled", "enabled"]).default("disabled"),
});

type BasicSettingsFormValues = z.infer<typeof basicSettingsSchema>;

/* ───────────────────────────────────────── props ───────────────────────────────────────── */
interface AgentBasicSettingsFormProps {
  agent: any;
  updateAgent: (data: Partial<any>) => Promise<boolean>;
  hideTitle?: boolean;
  enableAutoSave?: boolean;
  showOnly?: string[];
}

/* ───────────────────────────────────────── component ───────────────────────────────────────── */
const AgentBasicSettingsForm = ({
  agent,
  updateAgent,
  hideTitle = false,
  enableAutoSave = false,
  showOnly,
}: AgentBasicSettingsFormProps) => {
  const [initialFormValues, setInitialFormValues] =
    useState<BasicSettingsFormValues | null>({
      title: agent.title || "",
      description: agent.description || "",
      agent_name: agent.agent_name || "",
      welcome_message: agent.welcome_message || "",
      conversation_starters: agent.conversation_starters
        ? agent.conversation_starters.join("\n")
        : "",
      thank_you_message: agent.thank_you_message || "",
      notification_email: agent.notification_email || "",
      notification_webhook_url: agent.notification_webhook_url || "",
      allow_web_search: agent.allow_web_search || "disabled",
    });
  /* ---------- form ---------- */
  const basicForm = useForm<BasicSettingsFormValues>({
    resolver: zodResolver(basicSettingsSchema),
    defaultValues: {
      title: agent.title || "",
      description: agent.description || "",
      agent_name: agent.agent_name || "",
      welcome_message: agent.welcome_message || "",
      conversation_starters: agent.conversation_starters
        ? agent.conversation_starters.join("\n")
        : "",
      thank_you_message: agent.thank_you_message || "",
      notification_email: agent.notification_email || "",
      notification_webhook_url: agent.notification_webhook_url || "",
      allow_web_search: agent.allow_web_search || "disabled",
    },
  });

  /* ---------- autosave ---------- */
  const { isSaving, setDirty } = useAutoSave({
    onSave: async () => {
      if (!enableAutoSave || !initialFormValues) return;
      const data = basicForm.getValues();
      // Split starters into an array (one per line)
      const formattedData = {
        ...data,
        conversation_starters: data.conversation_starters
          ? data.conversation_starters
              .split("\n")
              .filter((s) => s.trim() !== "")
          : [],
      };
      return await updateAgent(formattedData);
    },
    successMessage: "Basic settings saved automatically",
    errorMessage: "Failed to auto-save basic settings",
  });

  /* ---------- watch for changes ---------- */
  useEffect(() => {
    if (!enableAutoSave || !initialFormValues) return;
    const handleChange = () => {
      const data = basicForm.getValues();
      const currentValues = {
        ...data,
        conversation_starters: data.conversation_starters || "",
      };
      // Compare as string for conversation_starters
      const hasChanges = Object.keys(currentValues).some((key) => {
        if (key === "conversation_starters") {
          return (
            currentValues.conversation_starters !==
            initialFormValues.conversation_starters
          );
        }
        return (
          currentValues[key as keyof BasicSettingsFormValues] !==
          initialFormValues[key as keyof BasicSettingsFormValues]
        );
      });
      if (hasChanges) {
        setDirty();
        setInitialFormValues(currentValues);
      }
    };
    const subscription = basicForm.watch(handleChange);
    return () => subscription.unsubscribe();
  }, [basicForm, enableAutoSave, setDirty, initialFormValues]);

  /* ---------- util ---------- */
  const shouldShowField = (fieldName: string) =>
    !showOnly || showOnly.includes(fieldName);

  /* ──────────────────────────────── render ──────────────────────────────── */
  return (
    <Form {...basicForm}>
      <form className="space-y-4">
        {/* Title */}
        {!hideTitle && shouldShowField("title") && (
          <FormField
            control={basicForm.control}
            name="title"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm font-medium text-gray-700">
                  Agent Title
                </FormLabel>
                <FormControl>
                  <Input placeholder="Enter agent title" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        {/* Agent name */}
        {shouldShowField("agent_name") && (
          <FormField
            control={basicForm.control}
            name="agent_name"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm font-medium text-gray-700">
                  Agent Name
                </FormLabel>
                <FormControl>
                  <Input
                    placeholder="Enter a friendly name for your agent"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        {/* Description */}
        {shouldShowField("description") && (
          <FormField
            control={basicForm.control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm font-medium text-gray-700">
                  Description
                </FormLabel>
                <FormControl>
                  <Input
                    placeholder="Describe your agent's purpose and capabilities"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        {/* Welcome message */}
        {shouldShowField("welcome_message") && (
          <FormField
            control={basicForm.control}
            name="welcome_message"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm font-medium text-gray-700">
                  Welcome Message
                </FormLabel>
                <FormControl>
                  <Textarea
                    placeholder="Shown to users when they first interact with your agent"
                    className="min-h-[60px]"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        {/* Conversation starters */}
        {shouldShowField("conversation_starters") && (
          <FormField
            control={basicForm.control}
            name="conversation_starters"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm font-medium text-gray-700">
                  Starter Options
                </FormLabel>
                <FormControl>
                  <Textarea
                    placeholder="One per line – displayed as buttons when the chat begins"
                    className="min-h-[60px]"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        {/* Thank you message */}
        {shouldShowField("thank_you_message") && (
          <FormField
            control={basicForm.control}
            name="thank_you_message"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm font-medium text-gray-700">
                  Thank You Message
                </FormLabel>
                <FormControl>
                  <Textarea
                    placeholder="Shown to users when they finish interacting with your agent"
                    className="min-h-[60px]"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        {/* Notification email */}
        {shouldShowField("notification_email") && (
          <FormField
            control={basicForm.control}
            name="notification_email"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm font-medium text-gray-700">
                  Notification Email
                </FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    placeholder="Email for agent notifications"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        {/* Notification webhook */}
        {shouldShowField("notification_webhook_url") && (
          <FormField
            control={basicForm.control}
            name="notification_webhook_url"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm font-medium text-gray-700">
                  Notification Webhook URL
                </FormLabel>
                <FormControl>
                  <Input
                    placeholder="Webhook URL for agent notifications"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        {/* Web search toggle */}
        {shouldShowField("allow_web_search") && (
          <FormField
            control={basicForm.control}
            name="allow_web_search"
            render={({ field }) => (
              <FormItem>
                <div className="flex items-center gap-2">
                  <FormLabel className="text-sm font-medium text-gray-700">
                    Web Search
                  </FormLabel>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger type="button">
                        <Info className="h-4 w-4 text-gray-500" />
                      </TooltipTrigger>
                      <TooltipContent className="p-3 max-w-xs bg-slate-800 text-white shadow-lg border border-slate-700 rounded-md">
                        <p className="text-sm">
                          Lets&nbsp;the agent pull fresh info from the internet.
                        </p>
                        <p className="mt-2 text-xs font-medium text-yellow-300">
                          (May increase API usage costs.)
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <FormControl>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger>
                      <SelectValue placeholder="Web search setting" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="disabled">Disabled</SelectItem>
                      <SelectItem value="enabled">Enabled</SelectItem>
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        {/* Auto‑save indicator */}
        {enableAutoSave && isSaving && (
          <div className="flex justify-end">
            <span className="text-sm text-muted-foreground">Auto‑saving…</span>
          </div>
        )}
      </form>
    </Form>
  );
};

export default AgentBasicSettingsForm;
